// ConversationHistory.js
// Final version with smaller New Chat and proper menu positioning

import React, { useState, useEffect, useRef } from 'react';
import '../styles/ConversationHistory.css';
import { conversationService } from '../services/conversationService';

const ConversationHistory = ({
  user,
  onConversationSelect,
  onNewConversation,
  currentConversationId,
  isCollapsed,
  onToggleCollapse
}) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [actionMenu, setActionMenu] = useState(null);

  const menuRef = useRef(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActionMenu(null);
      }
    };

    if (actionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [actionMenu]);

  const loadConversations = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await conversationService.listConversations(50, 0);
      if (data.success) {
        setConversations(data.conversations || []);
      } else {
        throw new Error('Failed to load conversations');
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Failed to load conversation history');
    } finally {
      setLoading(false);
    }
  };

  const handleConversationClick = async (conversation) => {
    try {
      const data = await conversationService.getConversation(conversation.conversation_id);

      if (data.success) {
        if (onConversationSelect) {
          onConversationSelect(data.conversation);
        }
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError('Failed to load conversation details');
    }
  };

  const handleRenameConversation = (conversation) => {
    setEditingTitleId(conversation.conversation_id);
    setEditTitleValue(conversation.title);
    setActionMenu(null);
  };

  const handleSaveTitle = async (conversationId) => {
    if (!editTitleValue.trim()) return;

    try {
      await conversationService.updateTitle(conversationId, editTitleValue);

      setConversations(conversations.map(conv =>
        conv.conversation_id === conversationId
          ? { ...conv, title: editTitleValue }
          : conv
      ));

      setEditingTitleId(null);
    } catch (err) {
      console.error('Error updating title:', err);
      setError('Failed to update conversation title');
    }
  };

  const handleCancelEdit = () => {
    setEditingTitleId(null);
    setEditTitleValue('');
  };

  const handleDeleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This cannot be undone.')) {
      return;
    }

    try {
      await conversationService.deleteConversation(conversationId);
      setConversations(conversations.filter(conv => conv.conversation_id !== conversationId));
      setActionMenu(null);
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError('Failed to delete conversation');
    }
  };

  const handlePinConversation = async (conversation) => {
    try {
      const newPinnedState = !conversation.is_pinned;
      await conversationService.pinConversation(conversation.conversation_id, newPinnedState);

      setConversations(conversations.map(conv =>
        conv.conversation_id === conversation.conversation_id
          ? { ...conv, is_pinned: newPinnedState }
          : conv
      ));

      setActionMenu(null);
    } catch (err) {
      console.error('Error pinning conversation:', err);
      setError('Failed to pin conversation');
    }
  };

  const handleActionMenuClick = (e, conversation) => {
    e.preventDefault();
    e.stopPropagation();

    if (actionMenu?.conversationId === conversation.conversation_id) {
      setActionMenu(null);
    } else {
      const buttonRect = e.currentTarget.getBoundingClientRect();
      const sidebarRect = sidebarRef.current?.getBoundingClientRect();
      
      // Calculate menu position
      // Position to the left of the button, aligned with button top
      const menuWidth = 140;
      let menuLeft = buttonRect.left - menuWidth - 8; // 8px gap
      let menuTop = buttonRect.top;
      
      // If menu would go off left edge, show on right side instead
      if (menuLeft < (sidebarRect?.left || 0)) {
        menuLeft = buttonRect.right + 8;
      }
      
      // Ensure menu doesn't go below viewport
      const menuHeight = 120; // approximate
      if (menuTop + menuHeight > window.innerHeight) {
        menuTop = window.innerHeight - menuHeight - 10;
      }
      
      const menuData = {
        conversationId: conversation.conversation_id,
        conversation: conversation,
        x: menuLeft,
        y: menuTop
      };
      setActionMenu(menuData);
    }
  };

  const sortedConversations = [...conversations].sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    return new Date(b.updated_at) - new Date(a.updated_at);
  });

  const formatDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <div className="conversation-history-section" ref={sidebarRef}>
        <div className="conversation-header">
          <h3 className="conversation-title">
            <span className="conversation-icon">💬</span>
            CONVERSATION HISTORY
          </h3>
        </div>

        {/* New Chat Button - Extra Small */}
        <button className="new-chat-btn-tiny" onClick={onNewConversation}>
          <span className="icon">➕</span>
          <span>New Chat</span>
        </button>

        <div className="conversations-list">
          {loading && (
            <div className="conversations-loading">
              <div className="loading-spinner-small"></div>
              <span>Loading...</span>
            </div>
          )}

          {error && (
            <div className="conversations-error">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
              <button onClick={loadConversations} className="retry-btn">Retry</button>
            </div>
          )}

          {!loading && !error && sortedConversations.length === 0 && (
            <div className="conversations-empty">
              <span className="empty-icon">💬</span>
              <p>No conversations yet</p>
            </div>
          )}

          {!loading && !error && sortedConversations.map((conversation) => (
            editingTitleId === conversation.conversation_id ? (
              <div key={conversation.conversation_id} className="conversation-item editing" onClick={(e) => e.stopPropagation()}>
                <div className="edit-title-container">
                  <input
                    type="text"
                    value={editTitleValue}
                    onChange={(e) => setEditTitleValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTitle(conversation.conversation_id);
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    className="edit-title-input"
                    autoFocus
                  />
                  <div className="edit-title-actions">
                    <button className="save-title-btn" onClick={() => handleSaveTitle(conversation.conversation_id)}>✓</button>
                    <button className="cancel-title-btn" onClick={handleCancelEdit}>×</button>
                  </div>
                </div>
              </div>
            ) : (
              <div key={conversation.conversation_id} className={`conversation-item-wrapper ${currentConversationId === conversation.conversation_id ? 'active' : ''}`}>
                <button className="conversation-item" onClick={() => handleConversationClick(conversation)}>
                  <div className="conversation-item-main">
                    <div className="conversation-item-title">
                      {conversation.is_pinned && <span className="pin-indicator">📌</span>}
                      {conversation.title || 'Untitled'}
                    </div>
                    <div className="conversation-item-preview">
                      {conversation.last_question || conversation.first_question || 'No messages'}
                    </div>
                    <div className="conversation-item-footer">
                      <span className="conversation-date">{formatDate(conversation.updated_at)}</span>
                    </div>
                  </div>
                </button>

                <button
                  className="conversation-menu-btn"
                  onClick={(e) => handleActionMenuClick(e, conversation)}
                  title="Options"
                  type="button"
                >
                  ⋮
                </button>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Menu with improved positioning */}
      {actionMenu && (
        <div
          ref={menuRef}
          className="conversation-action-menu"
          style={{
            position: 'fixed',
            left: `${actionMenu.x}px`,
            top: `${actionMenu.y}px`,
            zIndex: 999999
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="action-menu-item"
            onClick={(e) => {
              e.stopPropagation();
              handleRenameConversation(actionMenu.conversation);
            }}
          >
            <span className="menu-icon">✏️</span>
            <span>Rename</span>
          </button>

          <button
            className="action-menu-item"
            onClick={(e) => {
              e.stopPropagation();
              handlePinConversation(actionMenu.conversation);
            }}
          >
            <span className="menu-icon">
              {actionMenu.conversation.is_pinned ? '📍' : '📌'}
            </span>
            <span>{actionMenu.conversation.is_pinned ? 'Unpin' : 'Pin'}</span>
          </button>

          <div className="menu-divider"></div>

          <button
            className="action-menu-item danger"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteConversation(actionMenu.conversationId);
            }}
          >
            <span className="menu-icon">🗑️</span>
            <span>Delete</span>
          </button>
        </div>
      )}
    </>
  );
};

export default ConversationHistory;
